import { LoaderCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import type { PortfolioSummaryDto } from '../shared/types';
import { api, ApiClientError } from './api';
import { Shell } from './components/Shell';
import type { DisplayCurrency } from './format';
import { Dashboard } from './pages/Dashboard';
import { Landing } from './pages/Landing';
import { PrivacyPage, TermsPage } from './pages/Legal';
import { ReviewPage } from './pages/Review';
import { SettingsPage } from './pages/Settings';
import { TargetsPage } from './pages/Targets';
import { TransactionsPage } from './pages/Transactions';
import { UploadPage } from './pages/Upload';

type User = { id: string; displayName: string; pictureUrl: string | null; role: string; lineSubject: string };
type Config = { lineLoginConfigured: boolean; devLoginEnabled: boolean; liffId: string | null };

export default function App() {
  const legalPath = location.pathname === '/privacy' ? 'privacy' : location.pathname === '/terms' ? 'terms' : null;
  const [config, setConfig] = useState<Config | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [summary, setSummary] = useState<PortfolioSummaryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrencyState] = useState<DisplayCurrency>(() => localStorage.getItem('display-currency') === 'THB' ? 'THB' : 'USD');
  const summaryRequest = useRef(0);
  const summaryApplied = useRef(0);
  function setCurrency(value: DisplayCurrency) { setCurrencyState(value); localStorage.setItem('display-currency', value); }
  const reloadSummary = useCallback(async () => {
    const request = ++summaryRequest.current;
    const next = await api<PortfolioSummaryDto>('/portfolio/summary');
    if (request > summaryApplied.current) {
      summaryApplied.current = request;
      setSummary(next);
    }
  }, []);
  useEffect(() => {
    if (legalPath) { setLoading(false); return; }
    (async () => {
      try {
        const configValue = await api<Config>('/config'); setConfig(configValue);
        const userValue = await api<User>('/me'); setUser(userValue);
        await reloadSummary();
      } catch (error) {
        if (!(error instanceof ApiClientError) || error.status !== 401) console.error(error);
      } finally { setLoading(false); }
    })();
  }, [legalPath, reloadSummary]);
  if (legalPath === 'privacy') return <PrivacyPage />;
  if (legalPath === 'terms') return <TermsPage />;
  if (loading || !config) return <div className="loading-screen"><LoaderCircle className="spin" /><span>กำลังเปิด Private Wealth…</span></div>;
  if (!user) return <Landing config={config} />;
  return <BrowserRouter><Shell user={user} summary={summary}><Routes>
    <Route path="/" element={summary ? <Dashboard summary={summary} currency={currency} setCurrency={setCurrency} reload={reloadSummary} canLoadDemo={user.role === 'ADMIN'} isLocalDemo={config.devLoginEnabled && user.lineSubject === 'local-demo-user'} /> : <div className="loading-screen"><LoaderCircle className="spin" /></div>} />
    <Route path="/upload" element={<UploadPage reloadSummary={reloadSummary} />} />
    <Route path="/review" element={<ReviewPage reloadSummary={reloadSummary} />} />
    <Route path="/review/:id" element={<ReviewPage reloadSummary={reloadSummary} />} />
    <Route path="/transactions" element={<TransactionsPage reloadSummary={reloadSummary} />} />
    <Route path="/targets" element={summary ? <TargetsPage summary={summary} /> : null} />
    <Route path="/settings" element={<SettingsPage user={user} reloadSummary={reloadSummary} />} />
  </Routes></Shell></BrowserRouter>;
}
